import React, { useState } from "react";
import { updateCohort, getCohortbyCohortID, bulkEditCohorts, isConcurrentChange, versionsOf } from "../../services/cohort";
import Button from "../../components/inputs/button";
import BulkProgressIndicator from "./BulkProgressIndicator";
import ModalSurface from "./ModalSurface";
import useBatchOperation from "../../hooks/useBatchOperation";
import {
	BULK_CONFIRM_ESCALATION_THRESHOLD,
	BULK_EDIT_CHUNK_SIZE,
} from "../../utilities/constants";
import PropTypes from "prop-types";
import "./index.scss";
import x from "../../assets/svgs/x-green.svg";

function chunkArray(arr, size) {
	const out = [];
	for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
	return out;
}

function ArchiveConfirmation({ archiveType, cohort, onUpdate, selectedRows, getCohorts }) {
	const uniqueIds = cohort.map((cohort) => cohort.value);
	const [message, setMessage] = useState(null);
	const [showArchiveEditSuccess, setShowArchiveEditSuccess] = useState(false);
	const [showArchiveEditError, setShowArchiveEditError] = useState(false);
	const [type, setType] = useState(archiveType);
	const [tagId, setTagId] = useState(localStorage.getItem("activeSilo"));
	const [loading, setLoading] = useState(false);

	const { execute, cancel, state: batchState } = useBatchOperation({ batchSize: 1 });

	const isUnarchive = type === "Unarchive";
	const pastTenseVerb = isUnarchive ? "activated" : "archived";
	const presentVerb = isUnarchive ? "activate" : "archive";
	const count = uniqueIds.length;
	const escalate = count > BULK_CONFIRM_ESCALATION_THRESHOLD;

	const finishSuccess = (successMessage) => {
		// Reload now, not after the 2 s message: the rows' versions moved (ASB-4801).
		getCohorts(tagId);
		setMessage(successMessage);
		setShowArchiveEditSuccess(true);
		setLoading(false);
		setTimeout(() => {
			setShowArchiveEditSuccess(false);
			getCohorts(tagId);
			onUpdate();
			selectedRows("");
		}, 2000);
	};

	const handleEdit = async () => {
		setLoading(true);

		try {
			if (count === 1) {
				const id = uniqueIds[0];
				const data = await getCohortbyCohortID(id, tagId);
				const cohortData = data.data;
				cohortData.active = isUnarchive;
				// The version the list loaded, not this re-read's (ASB-4801).
				if (cohort[0]?.version != null) cohortData.version = cohort[0].version;
				await updateCohort({ data: [cohortData] }, id, tagId);
				finishSuccess(`Audience ${pastTenseVerb} successfully`);
				return;
			}

			const chunks = chunkArray(uniqueIds, BULK_EDIT_CHUNK_SIZE);
			const tasks = chunks.map((chunkIds, i) => ({
				id: `chunk-${i}`,
				run: () => bulkEditCohorts(chunkIds, { active: isUnarchive }, tagId, versionsOf(cohort.filter((c) => chunkIds.includes(c.value)))),
			}));

			const result = await execute(tasks);

			const cohortsSucceeded = Math.min(result.succeeded * BULK_EDIT_CHUNK_SIZE, count);
			const cohortsFailed = count - cohortsSucceeded;
			// ASB-4801: a part refused because someone else saved one of its audiences since
			// the list loaded says so in the API's words.
			const changed = (result.errors || []).find(isConcurrentChange);
			const changedNote = changed ? ` ${changed.data.message}` : "";

			if (result.cancelled) {
				setMessage(`Operation cancelled. ${cohortsSucceeded} of ${count} audience(s) ${pastTenseVerb}.`);
				setShowArchiveEditError(true);
				setLoading(false);
				return;
			}

			if (result.failed === 0) {
				finishSuccess(`Audience(s) ${pastTenseVerb}`);
			} else if (result.succeeded === 0) {
				setMessage(changed ? changed.data.message : `Error updating audience(s)`);
				setShowArchiveEditError(true);
				setLoading(false);
			} else {
				setMessage(`${cohortsSucceeded} of ${count} audience(s) ${pastTenseVerb}. ${cohortsFailed} failed.${changedNote}`);
				setShowArchiveEditError(true);
				setLoading(false);
			}
		} catch (error) {
			setMessage(isConcurrentChange(error)
				? error.data.message
				: `Error ${isUnarchive ? "activating" : "archiving"} audience(s)`);
			setShowArchiveEditError(true);
			setLoading(false);
		}
	};

	const renderPrompt = () => {
		if (escalate) {
			if (isUnarchive) {
				return `You are about to activate ${count} audiences. They will begin activating downstream immediately. Continue?`;
			}
			return `You are about to archive ${count} audiences. They will no longer activate downstream until you unarchive them. Continue?`;
		}
		return `Would you like to ${presentVerb} ${count} audience${count !== 1 ? "s" : ""}?`;
	};

	const progressCohortsCompleted = Math.min(batchState.completed * BULK_EDIT_CHUNK_SIZE, count);
	const progressLabel = `${progressCohortsCompleted} of ${count} audiences ${pastTenseVerb}`;

	if (showArchiveEditSuccess || showArchiveEditError) {
		const status = showArchiveEditSuccess ? "Success" : "Error";
		const dotClass = showArchiveEditSuccess ? "dot--Success" : "dot--Error";
		return (
			<div id="archive-box">
				<div className="toastContainer">
					<div className="toast--inner">
						<div className="toast--inner--head">
							<div>
								<span className={dotClass}> </span> {status}
							</div>
							<img
								src={x}
								className="close"
								alt="close"
								role={"button"}
								onClick={() => {
									// After an error too: rows that did change, or that someone else
									// saved, need their new versions before a retry (ASB-4801). The
									// selection stays, so the user can retry the rest; bulk actions
									// read versions from the reloaded table, not the selection.
									if (showArchiveEditError) getCohorts(tagId);
									onUpdate();
								}}
							/>
						</div>
						<div className="toast--inner--body">
							<p className="p3 no-margin">{message}</p>
						</div>
					</div>
				</div>
			</div>
		);
	}

	const title = isUnarchive ? "Activate Audiences" : "Archive Audiences";

	const footer = (
		<>
			<button
				type="button"
				className="cancel-link"
				onClick={() => onUpdate()}
				disabled={loading}
			>
				Cancel
			</button>
			<Button
				loaded={loading}
				onClick={handleEdit}
				text={isUnarchive ? "Activate" : "Archive"}
				style="dark"
				type="button"
				disabled={loading}
			/>
		</>
	);

	return (
		<ModalSurface title={title} onClose={onUpdate} footer={footer} closeDisabled={loading}>
			<p className="p3">{renderPrompt()}</p>
			{!escalate && (
				<div className="modal-audience-list p3">
					{cohort && cohort.map((cohort) => cohort.label).join("\n")}
				</div>
			)}
			<BulkProgressIndicator state={batchState} onCancel={cancel} label={progressLabel} />
		</ModalSurface>
	);
}

function ArchiveEditConfirmation() {
	return (
		<div className="confirmation-container">
			<div className="confirmation-box">
				<h1>Audiences have been updated!</h1>
			</div>
		</div>
	);
}

ArchiveConfirmation.propTypes = {
	onUpdate: PropTypes.func.isRequired,
	cohort: PropTypes.any,
	selectedRows: PropTypes.func,
	getCohorts: PropTypes.func,
	archiveType: PropTypes.any,
};

export { ArchiveConfirmation, ArchiveEditConfirmation };
